// apps/web/src/lib/vendas/recebimento-pv.ts — Fase 12, Planos 10 e 29 (NFE-05, NFE-06, D-08, D-12).
// Transformações puras da conferência da NF com item de PV: candidatos e sugestão FIFO vindos da API
// (`GET /vendas/recebimentos/:id/sugestoes`), divisão entre itens, excedente justificado e mensagens por item.
// Nenhuma regra de saldo é decidida aqui: o servidor valida cliente, código, saldo e excedente; a web só apresenta
// e impede o envio óbvio (soma que não fecha, excedente sem justificativa).
import type { AlocacaoPv, CandidatoItemPv, SugestaoRecebimentoItem } from '@pieltec/contracts';
import { formatarQuantidade } from './formatacao';

/** Valor do seletor para "Sem PV" (receber pela rota legada). */
export const SEM_PV = '';
/** Valor do seletor para "Dividir entre itens…" (várias alocações para o mesmo item da nota). */
export const DIVIDIR_ENTRE_ITENS = '__dividir__';

/** Mesmo mínimo de caracteres da justificativa de excedente que a API exige (trim >= 5). */
export const MINIMO_JUSTIFICATIVA = 5;
const TOLERANCIA = 0.00005;

/** Parte da quantidade física destinada a um item de PV, ainda em edição na tela. */
export interface AlocacaoEmEdicao {
  readonly pedidoVendaItemId: string;
  readonly quantidade: number;
  readonly saldoAReceber: number;
  readonly justificativa: string;
}

function prazoDiaMes(prazo: string): string | null {
  const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(prazo);
  return partes ? `${partes[3]}/${partes[2]}` : null;
}

/** 'PV-2026-0007 · item 2 · saldo 40 · prazo 05/10' (prazo nulo: 'sem prazo'). */
export function rotuloOpcaoItemPv(candidato: Pick<CandidatoItemPv, 'pvNumero' | 'numeroItem' | 'saldoAReceber' | 'prazo'>): string {
  const prazo = candidato.prazo ? prazoDiaMes(candidato.prazo) : null;
  return `${candidato.pvNumero} · item ${candidato.numeroItem} · saldo ${formatarQuantidade(candidato.saldoAReceber)} · ${prazo ? `prazo ${prazo}` : 'sem prazo'}`;
}

/** Quantidade acima do saldo a receber do item de PV (zero quando cabe). */
export function excedenteDaAlocacao(alocacao: { readonly quantidade: number; readonly saldoAReceber: number }): number {
  const excedente = alocacao.quantidade - alocacao.saldoAReceber;
  return excedente > TOLERANCIA ? excedente : 0;
}

/** A soma das partes precisa igualar a quantidade física conferida; partes negativas ou não finitas invalidam. */
export function divisaoValida(quantidades: readonly number[], total: number): boolean {
  if (!Number.isFinite(total) || total <= 0) return false;
  if (quantidades.some((quantidade) => !Number.isFinite(quantidade) || quantidade < 0)) return false;
  const soma = quantidades.reduce((acumulado, quantidade) => acumulado + quantidade, 0);
  return Math.abs(soma - total) < TOLERANCIA;
}

/** True quando alguma alocação tem excedente e a justificativa (trim) tem menos de 5 caracteres. */
export function precisaJustificativa(
  alocacoes: readonly { readonly quantidade: number; readonly saldoAReceber: number; readonly justificativa?: string | null }[],
): boolean {
  return alocacoes.some((alocacao) => excedenteDaAlocacao(alocacao) > 0 && (alocacao.justificativa ?? '').trim().length < MINIMO_JUSTIFICATIVA);
}

/** Alerta amber do excedente (D-12). */
export function mensagemExcedente(excedente: number): string {
  return `Quantidade acima do saldo do item de PV (+${formatarQuantidade(excedente)}). Informe a justificativa para continuar.`;
}

/** Mensagem por item depois de confirmar: modo de observação, sem roteiro ou OP criada na Fila de entrada. */
export function mensagemResultadoItem(resultado: { readonly opNumero?: number | null; readonly semRoteiro?: boolean; readonly modoObservacao?: boolean }): string {
  if (resultado.modoObservacao) return 'Modo de observação ativo: a OP não é criada automaticamente.';
  if (resultado.semRoteiro) {
    return 'Recebimento registrado. A OP não foi criada porque o material não tem roteiro vigente. Cadastre o roteiro da peça para gerar a OP.';
  }
  if (resultado.opNumero !== null && resultado.opNumero !== undefined) {
    return `Recebimento registrado. OP ${resultado.opNumero} criada e enviada à Fila de entrada do Kanban.`;
  }
  return 'Recebimento registrado.';
}

/** Candidatos por `documentoFiscalItemId` (a API já os devolve em ordem FIFO). */
export function candidatosPorItemDasSugestoes(sugestoes: readonly SugestaoRecebimentoItem[]): Record<string, readonly CandidatoItemPv[]> {
  const saida: Record<string, readonly CandidatoItemPv[]> = {};
  for (const sugestao of sugestoes) saida[sugestao.documentoFiscalItemId] = sugestao.candidatos;
  return saida;
}

/** Transforma a sugestão da API em alocações editáveis (uma por parte sugerida). */
export function alocacoesDaSugestao(candidatos: readonly CandidatoItemPv[], sugestao: readonly AlocacaoPv[]): AlocacaoEmEdicao[] {
  return sugestao.map((parte) => ({
    pedidoVendaItemId: parte.pedidoVendaItemId,
    quantidade: parte.quantidade,
    saldoAReceber: candidatos.find((candidato) => candidato.pedidoVendaItemId === parte.pedidoVendaItemId)?.saldoAReceber ?? Number.POSITIVE_INFINITY,
    justificativa: parte.justificativaExcedente ?? '',
  }));
}

/** FIFO pré-selecionado: uma parte sugerida vira o item de PV; várias viram divisão pré-preenchida; nenhuma fica sem PV. */
export function selecaoInicialDasSugestoes(sugestoes: readonly SugestaoRecebimentoItem[]): {
  selecao: Record<string, string>;
  divisoes: Record<string, AlocacaoEmEdicao[]>;
} {
  const selecao: Record<string, string> = {};
  const divisoes: Record<string, AlocacaoEmEdicao[]> = {};
  for (const sugestao of sugestoes) {
    const partes = sugestao.sugestao.filter((parte) => sugestao.candidatos.some((candidato) => candidato.pedidoVendaItemId === parte.pedidoVendaItemId));
    if (partes.length === 1 && partes[0]) {
      selecao[sugestao.documentoFiscalItemId] = partes[0].pedidoVendaItemId;
    } else if (partes.length > 1) {
      selecao[sugestao.documentoFiscalItemId] = DIVIDIR_ENTRE_ITENS;
      divisoes[sugestao.documentoFiscalItemId] = alocacoesDaSugestao(sugestao.candidatos, partes);
    }
  }
  return { selecao, divisoes };
}

export interface ItemConferido {
  readonly documentoFiscalItemId: string;
  readonly quantidadeFisica: number;
  readonly materialId?: string;
  readonly loteClienteRef?: string;
}

export interface ItemConfirmacaoPv extends ItemConferido {
  readonly alocacoes?: readonly AlocacaoPv[];
}

/** Estado da conferência que acompanha a seleção: candidatos, justificativa por item e divisões editadas. */
export interface ContextoConfirmacao {
  readonly candidatosPorItem: Readonly<Record<string, readonly CandidatoItemPv[]>>;
  readonly justificativas: Readonly<Record<string, string>>;
  readonly divisoes: Readonly<Record<string, readonly AlocacaoEmEdicao[]>>;
}

const CONTEXTO_VAZIO: ContextoConfirmacao = { candidatosPorItem: {}, justificativas: {}, divisoes: {} };

/** True quando algum item tem item de PV escolhido (ou divisão): só então a confirmação usa a rota `vendas/recebimentos`. */
export function temAlocacao(selecao: Readonly<Record<string, string>>, itens: readonly ItemConferido[]): boolean {
  return itens.some((item) => (selecao[item.documentoFiscalItemId] ?? SEM_PV) !== SEM_PV);
}

/** Alocações em edição do item da nota; `null` quando o item é recebido sem PV. */
export function alocacoesEmEdicaoDoItem(
  selecao: Readonly<Record<string, string>>,
  item: ItemConferido,
  contexto: ContextoConfirmacao = CONTEXTO_VAZIO,
): readonly AlocacaoEmEdicao[] | null {
  const escolha = selecao[item.documentoFiscalItemId] ?? SEM_PV;
  if (escolha === SEM_PV) return null;
  if (escolha === DIVIDIR_ENTRE_ITENS) return contexto.divisoes[item.documentoFiscalItemId] ?? [];
  const candidato = contexto.candidatosPorItem[item.documentoFiscalItemId]?.find((c) => c.pedidoVendaItemId === escolha);
  return [
    {
      pedidoVendaItemId: escolha,
      quantidade: item.quantidadeFisica,
      saldoAReceber: candidato?.saldoAReceber ?? Number.POSITIVE_INFINITY,
      justificativa: contexto.justificativas[item.documentoFiscalItemId] ?? '',
    },
  ];
}

/** WR-D2-05: aviso que acompanha o bloqueio de um item de PV sem material (a confirmação não segue). */
export const MENSAGEM_ITEM_PV_SEM_MATERIAL =
  'Este item do PV não tem material vinculado, então o recebimento fica bloqueado. Escolha outro item de PV ou "Sem PV", ou peça a quem edita o PV (pv:editar) para vincular o material ao item e volte a esta conferência.';

/**
 * Itens de PV escolhidos (item único ou partes da divisão) que não têm material vinculado. O lote nasceria sem material, e
 * o servidor recusa ou aceita conforme a regra do dia: a tela não deixa confirmar e orienta como resolver (WR-D2-05).
 */
export function candidatosSemMaterialEscolhidos(
  selecao: Readonly<Record<string, string>>,
  itens: readonly ItemConferido[],
  contexto: ContextoConfirmacao = CONTEXTO_VAZIO,
): CandidatoItemPv[] {
  const encontrados: CandidatoItemPv[] = [];
  for (const item of itens) {
    const alocacoes = alocacoesEmEdicaoDoItem(selecao, item, contexto);
    if (alocacoes === null) continue;
    const candidatos = contexto.candidatosPorItem[item.documentoFiscalItemId] ?? [];
    for (const alocacao of alocacoes) {
      const candidato = candidatos.find((c) => c.pedidoVendaItemId === alocacao.pedidoVendaItemId);
      if (candidato !== undefined && candidato.materialId === null) encontrados.push(candidato);
    }
  }
  return encontrados;
}

/** True quando a conferência não pode ser enviada: divisão que não fecha, excedente sem justificativa ou item de PV sem material. */
export function conferenciaBloqueada(selecao: Readonly<Record<string, string>>, itens: readonly ItemConferido[], contexto: ContextoConfirmacao = CONTEXTO_VAZIO): boolean {
  if (candidatosSemMaterialEscolhidos(selecao, itens, contexto).length > 0) return true;
  return itens.some((item) => {
    const alocacoes = alocacoesEmEdicaoDoItem(selecao, item, contexto);
    if (alocacoes === null) return false;
    if ((selecao[item.documentoFiscalItemId] ?? SEM_PV) === DIVIDIR_ENTRE_ITENS && !divisaoValida(alocacoes.map((a) => a.quantidade), item.quantidadeFisica)) return true;
    return precisaJustificativa(alocacoes);
  });
}

/**
 * Itens da confirmação: com PV escolhido, toda a quantidade física vai para o item de PV; com divisão, uma alocação por
 * parte > 0. A justificativa só é enviada quando há excedente (a API é quem valida).
 */
export function montarItensConfirmacao(selecao: Readonly<Record<string, string>>, itens: readonly ItemConferido[], contexto: ContextoConfirmacao = CONTEXTO_VAZIO): ItemConfirmacaoPv[] {
  return itens.map((item) => {
    const alocacoes = alocacoesEmEdicaoDoItem(selecao, item, contexto);
    if (alocacoes === null) return item;
    const partes: AlocacaoPv[] = alocacoes
      .filter((alocacao) => alocacao.quantidade > 0)
      .map((alocacao) => {
        const justificativa = alocacao.justificativa.trim();
        return excedenteDaAlocacao(alocacao) > 0 && justificativa !== ''
          ? { pedidoVendaItemId: alocacao.pedidoVendaItemId, quantidade: alocacao.quantidade, justificativaExcedente: justificativa }
          : { pedidoVendaItemId: alocacao.pedidoVendaItemId, quantidade: alocacao.quantidade };
      });
    // O material vem do item de PV: o servidor ignora o `materialId` digitado quando há alocação.
    return { documentoFiscalItemId: item.documentoFiscalItemId, quantidadeFisica: item.quantidadeFisica, loteClienteRef: item.loteClienteRef, alocacoes: partes };
  });
}

// ---------------------------------------------------------------------------
// Reabertura da conferência por `?nota=` (WR-D2-06)
// ---------------------------------------------------------------------------

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type NotaDoLink = { readonly tipo: 'ausente' } | { readonly tipo: 'invalido' } | { readonly tipo: 'ok'; readonly id: string };

/** `id` da nota do parâmetro `?nota=`: ausente, inválido (não é UUID) ou válido. */
export function notaDoLink(search: string): NotaDoLink {
  const valor = new URLSearchParams(search).get('nota');
  if (valor === null || valor.trim() === '') return { tipo: 'ausente' };
  return UUID.test(valor.trim()) ? { tipo: 'ok', id: valor.trim() } : { tipo: 'invalido' };
}

export const MENSAGEM_NOTA_LINK_INVALIDO = 'O link da nota é inválido. Importe a nota novamente ou abra-a pela lista de remessas.';
export const MENSAGEM_NOTA_JA_CONFIRMADA =
  'Esta nota já teve um recebimento confirmado. Confirmar de novo registra OUTRO recebimento físico (parcial) da mesma nota, com novos lotes. Se você só queria consultar, feche esta janela.';
export const ROTULO_CIENTE_NOVO_RECEBIMENTO = 'Estou ciente: será um novo recebimento desta nota.';

/**
 * Nota com recebimento já confirmado. O servidor aceita recebimentos parciais sequenciais da mesma nota, então a tela NÃO bloqueia:
 * exige a ciência explícita antes de registrar outro recebimento (evita reconfirmar por engano ao reabrir o link).
 */
export function notaJaConfirmada(estado: string | null | undefined): boolean {
  return estado === 'CONFIRMADO';
}
